import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Film, X, LogOut, History, Play, Pause, VolumeX } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import axios from 'axios';

import { useAuthStore } from '../lib/authStore';
import AuthModal from '../components/AuthModal';
import PricingModal from '../components/PricingModal';
import DualEngagementCurve from '../components/DualEngagementCurve';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const T = {
  bg:           '#FFFFFF',
  bgSubtle:     '#FAFAFA',
  bgElevated:   '#F4F4F5',
  border:       '#E4E4E7',
  borderHover:  '#D4D4D8',
  fg:           '#09090B',
  muted:        '#71717A',
  subtle:       '#A1A1AA',
  placeholder:  '#D4D4D8',
  primary:      '#18181B',
  primaryFg:    '#FAFAFA',
  error:        '#EF4444',
  accent:       '#6366F1',
  accentDark:   '#4F46E5',
  accentSubtle: '#EEF2FF',
  accentBorder: '#C7D2FE',
};

// ── Pre-computed demo battle ─────────────────────────────────
const DEMO_A_ATTN = [42,45,48,52,55,57,58,56,54,52,50,48,28,25,27,32,37,41,44,46,48,50,52,54,55,53,51,49,48,47];
const DEMO_B_ATTN = [46,48,47,50,53,56,58,60,62,63,64,62,60,59,61,63,65,67,68,70,71,70,68,66,64,62,60,58,56,55];

const DEMO_BATTLE = {
  winner: 'B',
  winner_reason: "Creative B holds 13.6 pts higher neural activation with zero attention drops. Creative A loses 44% activation at the price reveal (0:12) — the audience disengages before the CTA.",
  label_a: 'Summer Cut v1',
  label_b: 'Summer Cut v2',
  side_a: {
    tribe_v2_ready: true,
    name: 'Summer Cut v1',
    avg_activation: 46.8,
    baseline_activation: 45.0,
    peak_second: 6,
    n_timesteps: 30,
    drops: [{ start_second: 12, end_second: 15, drop_pct: -44.4 }],
    attention: DEMO_A_ATTN,
  },
  side_b: {
    tribe_v2_ready: true,
    name: 'Summer Cut v2',
    avg_activation: 60.4,
    baseline_activation: 47.0,
    peak_second: 20,
    n_timesteps: 30,
    drops: [],
    attention: DEMO_B_ATTN,
  },
  timeline: DEMO_A_ATTN.map((a, i) => ({ t: i, a, b: DEMO_B_ATTN[i] })),
};

// ── Production limits ─────────────────────────────────────────
const MAX_FILE_MB  = 300;
const MAX_FILE_B   = MAX_FILE_MB * 1024 * 1024;
const ACCEPTED_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo', 'video/x-matroska'];

function validateVideoFile(file) {
  if (!file) return null;
  if (!file.type.startsWith('video/') && !ACCEPTED_TYPES.includes(file.type)) {
    return `Only video files are accepted (MP4, MOV, WebM).`;
  }
  if (file.size > MAX_FILE_B) {
    return `File too large — max ${MAX_FILE_MB} MB. Compress or trim your ad first.`;
  }
  return null; // valid
}

// ── Upload slot ──────────────────────────────────────────────
function UploadSlot({ side, file, url, onFile, onUrl, onClear, drag, onDragOver, onDragLeave, onDrop, fileError }) {
  const fileRef = useRef(null);
  const filled  = !!(file || url.trim());

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{
          width: 24, height: 24, borderRadius: 6,
          background: filled ? T.accent : T.bgElevated,
          border: `1px solid ${filled ? T.accent : T.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s',
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: filled ? '#fff' : T.muted }}>
            {side}
          </span>
        </div>
        <span style={{ fontSize: 12, fontWeight: 500, color: filled ? T.fg : T.muted }}>Ad {side}</span>
        {filled && (
          <button onClick={onClear} style={{
            marginLeft: 'auto', background: 'none', border: 'none',
            cursor: 'pointer', color: T.subtle,
            display: 'flex', alignItems: 'center', gap: 3, fontSize: 11,
          }}>
            <X size={10} /> Clear
          </button>
        )}
      </div>

        <div style={{
        border: `1.5px solid ${fileError ? '#F87171' : filled ? T.accentBorder : drag ? T.accentBorder : T.border}`,
        borderRadius: 12,
        background: fileError ? '#FFF5F5' : filled ? '#F5F3FF' : 'rgba(255,255,255,0.7)',
        overflow: 'hidden', transition: 'border-color 0.15s, background 0.15s',
      }}>
        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
          style={{
            padding: '22px 20px', textAlign: 'center', cursor: 'pointer',
            borderBottom: `1px solid ${T.border}`,
            background: drag ? T.bgSubtle : 'transparent',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = T.bgSubtle; }}
          onMouseLeave={e => { if (!drag) e.currentTarget.style.background = 'transparent'; }}
        >
          {file ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <Film size={16} style={{ color: fileError ? '#EF4444' : T.muted }} />
              <p style={{ fontSize: 12, color: fileError ? '#EF4444' : T.fg, fontWeight: 500, margin: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {file.name}
              </p>
              {fileError && (
                <p style={{ fontSize: 11, color: '#EF4444', margin: '2px 0 0', lineHeight: 1.4 }}>{fileError}</p>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: T.accentSubtle, border: `1px solid ${T.accentBorder}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Upload size={14} style={{ color: T.accent }} />
              </div>
              <p style={{ fontSize: 12, color: T.fg, fontWeight: 500, margin: '0 0 2px' }}>Drop your ad here</p>
              <p style={{ fontSize: 11, color: T.subtle, margin: 0 }}>MP4, MOV · max {MAX_FILE_MB} MB · max 2 min</p>
            </div>
          )}
          <input ref={fileRef} type="file" accept="video/*" style={{ display: 'none' }}
            onChange={e => onFile(e.target.files?.[0] || null)} />
        </div>

        <div style={{ padding: '10px 14px' }}>
          <input
            type="text" value={url} onChange={e => onUrl(e.target.value)}
            placeholder="youtube.com/watch?v=…"
            disabled={!!file}
            style={{
              width: '100%',
              background: file ? T.bgElevated : T.bgSubtle,
              border: `1px solid ${url.trim() ? T.borderHover : T.border}`,
              borderRadius: 6, padding: '7px 10px',
              fontSize: 12, color: file ? T.placeholder : T.fg,
              outline: 'none', cursor: file ? 'not-allowed' : 'text',
              fontFamily: 'Inter, sans-serif', transition: 'border-color 0.15s',
            }}
            onFocus={e => { if (!file) e.target.style.borderColor = T.accentBorder; }}
            onBlur={e => { e.target.style.borderColor = url.trim() ? T.accentBorder : T.border; }}
          />
        </div>
      </div>
    </div>
  );
}

// ── Demo section ─────────────────────────────────────────────
function DemoSection() {
  const [currentTime, setCurrentTime] = useState(0);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ flex: 1, height: 1, background: T.border }} />
        <p style={{
          fontSize: 10, fontFamily: "'IBM Plex Mono', monospace",
          letterSpacing: '0.12em', color: T.accent, margin: 0, fontWeight: 600,
        }}>
          EXAMPLE OUTPUT
        </p>
        <div style={{ flex: 1, height: 1, background: T.border }} />
      </div>

      <div style={{
        background: T.bg, border: `1px solid ${T.border}`,
        borderRadius: 20, padding: '28px 32px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          marginBottom: 20, flexWrap: 'wrap', gap: 12,
        }}>
          <div>
            <p style={{ fontSize: 14, fontWeight: 600, color: T.fg, margin: '0 0 3px' }}>
              Summer Cut v1 <span style={{ color: T.subtle, fontWeight: 400 }}>vs</span> Summer Cut v2
            </p>
            <p style={{ fontSize: 11, color: T.subtle, margin: 0 }}>
              30-second ad · TRIBE V2 fMRI
            </p>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: '#F0FDF4', border: '1px solid #BBF7D0',
            borderRadius: 8, padding: '6px 12px',
          }}>
            <span style={{ fontSize: 9, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: '0.1em', color: '#166534' }}>
              WINNER
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#15803D' }}>
              Summer Cut v2
            </span>
          </div>
        </div>

        <DualEngagementCurve
          timeline={DEMO_BATTLE.timeline}
          sideA={{ ...DEMO_BATTLE.side_a, name: DEMO_BATTLE.label_a }}
          sideB={{ ...DEMO_BATTLE.side_b, name: DEMO_BATTLE.label_b }}
          currentTime={currentTime}
          onTimeChange={setCurrentTime}
        />

        <div style={{
          marginTop: 16, padding: '12px 16px',
          background: T.bgSubtle, borderRadius: 8, border: `1px solid ${T.border}`,
        }}>
          <p style={{ fontSize: 12, color: T.muted, margin: 0, lineHeight: 1.6 }}>
            <span style={{ color: T.fg, fontWeight: 500 }}>Neural verdict — </span>
            {DEMO_BATTLE.winner_reason}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Video demo block ──────────────────────────────────────────
function VideoDemo() {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const inView = useInView(containerRef, { once: true, amount: 0.3 });
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (inView && videoRef.current && !started) {
      videoRef.current.play().then(() => { setPlaying(true); setStarted(true); }).catch(() => {});
    }
  }, [inView, started]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) { videoRef.current.pause(); setPlaying(false); }
    else { videoRef.current.play(); setPlaying(true); }
  };

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5 }}
      style={{ marginBottom: 56 }}
    >
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <p style={{
          fontSize: 10, fontFamily: "'IBM Plex Mono', monospace",
          letterSpacing: '0.12em', color: T.accent,
          textTransform: 'uppercase', margin: '0 0 10px', fontWeight: 600,
        }}>See it in action</p>
        <h2 style={{
          fontSize: 'clamp(1.4rem, 4vw, 2.2rem)',
          fontWeight: 800, letterSpacing: '-0.04em',
          color: T.fg, margin: 0, lineHeight: 1.1,
        }}>A real test, start to finish</h2>
      </div>

      <div style={{
        position: 'relative', borderRadius: 16, overflow: 'hidden',
        border: `1px solid ${T.border}`,
        boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
        background: '#000',
      }}>
        <video
          ref={videoRef}
          src="/videos/eonik-ab.mp4"
          muted={muted}
          loop
          playsInline
          style={{ width: '100%', display: 'block', maxHeight: 480, objectFit: 'cover' }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
        />

        {/* Controls overlay */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: '32px 20px 16px',
          background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <button
            onClick={togglePlay}
            style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#fff',
            }}
          >
            {playing ? <Pause size={15} fill="#fff" /> : <Play size={15} fill="#fff" />}
          </button>
          <button
            onClick={() => {
              const next = !muted;
              setMuted(next);
              if (videoRef.current) videoRef.current.muted = next;
            }}
            style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#fff',
            }}
          >
            <VolumeX size={13} style={{ opacity: muted ? 1 : 0.4 }} />
          </button>
          <span style={{
            fontSize: 10, color: 'rgba(255,255,255,0.6)',
            fontFamily: "'IBM Plex Mono', monospace", letterSpacing: '0.08em',
            marginLeft: 'auto',
          }}>A/B · Powered by Tribe V2</span>
        </div>
      </div>
    </motion.div>
  );
}

// ── Main page ────────────────────────────────────────────────
export default function Landing() {
  const navigate = useNavigate();
  const [fileA, setFileA]   = useState(null);
  const [fileB, setFileB]   = useState(null);
  const [urlA,  setUrlA]    = useState('');
  const [urlB,  setUrlB]    = useState('');
  const [dragA, setDragA]   = useState(false);
  const [dragB, setDragB]   = useState(false);
  const [fileErrA, setFileErrA] = useState('');
  const [fileErrB, setFileErrB] = useState('');
  const [loading,    setLoading]    = useState(false);
  const [uploadPct,  setUploadPct]  = useState(0);   // 0-100 during file upload
  const [error,      setError]      = useState('');
  const [authOpen,    setAuthOpen]    = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);

  // after login, should we auto-run?
  const pendingRunRef = useRef(false);

  const { user, isAuthenticated, logout, refreshUser } = useAuthStore();

  const filledA = !!(fileA || urlA.trim());
  const filledB = !!(fileB || urlB.trim());
  const hasFileError = !!(fileErrA || fileErrB);
  const canRun  = filledA && filledB && !loading && !hasFileError;

  const run = useCallback(async () => {
    setError('');
    setLoading(true);
    setUploadPct(0);
    try {
      const fd = new FormData();
      if (fileA) fd.append('file_a', fileA);
      else        fd.append('url_a', urlA.trim());
      if (fileB) fd.append('file_b', fileB);
      else        fd.append('url_b', urlB.trim());
      fd.append('label_a', fileA?.name?.split('.')[0] || 'Creative A');
      fd.append('label_b', fileB?.name?.split('.')[0] || 'Creative B');

      const r = await axios.post(`${BACKEND_URL}/api/tribe/battle`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100));
        },
      });

      // Navigate immediately — don't block on refreshUser
      navigate(`/battle/${r.data.battle_id}`);
      refreshUser().catch(() => {});
    } catch (e) {
      if (e.response?.status === 402) {
        setPricingOpen(true);
      } else if (e.response?.status === 401) {
        setAuthOpen(true);
      } else if (e.response?.status === 429) {
        setError(e.response?.data?.detail || 'You already have a test running. Wait for it to finish before starting another.');
      } else if (e.response?.status === 413) {
        setError(e.response?.data?.detail || 'One of your videos is too large. Max file size is 300 MB.');
      } else {
        setError(e.response?.data?.detail || 'Failed to start battle. Try again.');
      }
      setLoading(false);
      setUploadPct(0);
    }
  }, [fileA, fileB, urlA, urlB, navigate, refreshUser]);

  const handleRunClick = () => {
    if (!isAuthenticated) {
      pendingRunRef.current = canRun;
      setAuthOpen(true);
      return;
    }
    if ((user?.battles_remaining || 0) < 1) {
      setPricingOpen(true);
      return;
    }
    run();
  };

  // After login: if user had files ready, run automatically
  const handleAuthSuccess = useCallback(async () => {
    setAuthOpen(false);
    const { user: freshUser } = useAuthStore.getState();
    if (pendingRunRef.current) {
      pendingRunRef.current = false;
      if ((freshUser?.battles_remaining || 0) < 1) {
        setPricingOpen(true);
      } else {
        run();
      }
    }
  }, [run]);

  // After purchase: run automatically if files are ready
  const handlePurchased = useCallback(() => {
    setPricingOpen(false);
    if (filledA && filledB) run();
  }, [filledA, filledB, run]);

  const handleFileA = (f) => {
    if (!f) { setFileA(null); setFileErrA(''); return; }
    setFileA(f);
    setFileErrA(validateVideoFile(f) || '');
    setUrlA('');
  };
  const handleFileB = (f) => {
    if (!f) { setFileB(null); setFileErrB(''); return; }
    setFileB(f);
    setFileErrB(validateVideoFile(f) || '');
    setUrlB('');
  };

  const onDropA = e => {
    e.preventDefault(); setDragA(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFileA(f);
  };
  const onDropB = e => {
    e.preventDefault(); setDragB(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFileB(f);
  };

  const buttonLabel = () => {
    if (loading) return null;
    if (!filledA || !filledB) return 'Add two ads to compare';
    return 'Find the winner →';
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #FAFAFE 0%, #FDFDFF 20%, #FFFFFF 44%)',
        fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
      }}
    >
      {/* ── Nav ── */}
      <nav style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 28px',
        borderBottom: `1px solid ${T.border}`,
        background: 'rgba(255,255,255,0.88)',
        backdropFilter: 'blur(16px)',
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <a href="https://eonik.ai" style={{ display: 'flex', alignItems: 'center' }}>
          <img src="/assets/logo.svg" alt="eonik" style={{ height: 18, display: 'block' }} />
        </a>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <a
            href="https://join.slack.com/t/theadgene/shared_invite/zt-3wd4nva82-ow~P0FJd7TqbLD9aIAvZlQ"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px',
              background: '#4A154B',
              border: '1px solid #4A154B',
              borderRadius: 100,
              cursor: 'pointer',
              color: '#fff',
              fontSize: 11,
              fontWeight: 600,
              fontFamily: "'IBM Plex Mono', monospace",
              letterSpacing: '0.06em',
              textDecoration: 'none',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
              <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
            </svg>
            COMMUNITY
          </a>

          {isAuthenticated ? (
            <>
              <button
                onClick={() => navigate('/history')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '5px 12px',
                  background: 'none', border: `1px solid ${T.border}`,
                  borderRadius: 100, cursor: 'pointer',
                  fontSize: 11, fontWeight: 500, color: T.muted,
                  fontFamily: "'IBM Plex Mono', monospace",
                  letterSpacing: '0.06em', transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = T.fg; e.currentTarget.style.borderColor = T.borderHover; }}
                onMouseLeave={e => { e.currentTarget.style.color = T.muted; e.currentTarget.style.borderColor = T.border; }}
              >
                <History size={11} /> HISTORY
              </button>

              <button
                onClick={() => setPricingOpen(true)}
                style={{
                  padding: '5px 12px',
                  background: (user?.battles_remaining || 0) > 0 ? T.accentSubtle : T.bgElevated,
                  border: `1px solid ${(user?.battles_remaining || 0) > 0 ? T.accentBorder : T.border}`,
                  borderRadius: 100, cursor: 'pointer',
                  fontSize: 12, fontWeight: 600,
                  color: (user?.battles_remaining || 0) > 0 ? T.accent : T.muted,
                  fontFamily: "'IBM Plex Mono', monospace",
                  letterSpacing: '0.04em',
                  transition: 'all 0.15s',
                }}
              >
                {user?.battles_remaining || 0} test{(user?.battles_remaining || 0) !== 1 ? 's' : ''} left
              </button>

              {user?.avatar_url && (
                <img src={user.avatar_url} alt={user.full_name}
                  style={{ width: 28, height: 28, borderRadius: '50%', border: `1.5px solid ${T.border}` }} />
              )}

              <button
                onClick={logout}
                title="Sign out"
                style={{
                  display: 'flex', alignItems: 'center',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: T.subtle, transition: 'color 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.color = T.muted)}
                onMouseLeave={e => (e.currentTarget.style.color = T.subtle)}
              >
                <LogOut size={14} />
              </button>
            </>
          ) : (
            <button
              onClick={() => setAuthOpen(true)}
              style={{
                padding: '7px 16px',
                background: 'none', color: T.muted,
                border: `1px solid ${T.border}`, borderRadius: 8,
                fontSize: 13, fontWeight: 500, cursor: 'pointer',
                fontFamily: 'Inter, sans-serif', transition: 'color 0.15s, border-color 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = T.fg; e.currentTarget.style.borderColor = T.borderHover; }}
              onMouseLeave={e => { e.currentTarget.style.color = T.muted; e.currentTarget.style.borderColor = T.border; }}
            >
              Sign in
            </button>
          )}
        </div>
      </nav>

      {/* ── Main content ── */}
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 24px 120px' }}>

        {/* Hero — immediate above fold, form directly below */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          style={{ textAlign: 'center', padding: '42px 0 22px' }}
        >
          <h1 style={{
            fontSize: 'clamp(2.1rem, 5.2vw, 3.2rem)',
            fontWeight: 800, letterSpacing: '-0.05em',
            color: T.fg, margin: '0 auto 14px', lineHeight: 1.05,
          }}>
            Find your winning ad before you spend.
          </h1>
          <p style={{
            fontSize: 15, color: T.muted, margin: '0 auto 16px',
            lineHeight: 1.45, maxWidth: 500, fontWeight: 400,
          }}>
            Compare two ads and find out which one to run.
          </p>
          <p style={{
            fontSize: 11, color: T.subtle,
            fontFamily: "'IBM Plex Mono', monospace",
            letterSpacing: '0.06em', margin: 0,
          }}>
            Built on real brain-scan data&nbsp;&nbsp;·&nbsp;&nbsp;Results in minutes&nbsp;&nbsp;·&nbsp;&nbsp;Plans from $19
          </p>
        </motion.div>

        {/* ── Upload form — primary action, immediately below hero ── */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          style={{ marginBottom: 16 }}
        >
          <div style={{
            background: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: `1px solid ${T.border}`,
            borderRadius: 20, padding: 24,
            boxShadow: '0 4px 32px rgba(99,102,241,0.08), 0 1px 4px rgba(0,0,0,0.04)',
          }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <UploadSlot
                side="A" file={fileA} url={urlA}
                onFile={handleFileA}
                onUrl={v => { setUrlA(v); if (v) { setFileA(null); setFileErrA(''); } }}
                onClear={() => { setFileA(null); setUrlA(''); setFileErrA(''); }}
                drag={dragA}
                onDragOver={e => { e.preventDefault(); setDragA(true); }}
                onDragLeave={() => setDragA(false)}
                onDrop={onDropA}
                fileError={fileErrA}
              />

              <div style={{
                flexShrink: 0, paddingTop: 34,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  border: `1px solid ${T.accentBorder}`,
                  background: T.accentSubtle,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontSize: 8, fontWeight: 800, color: T.accent, letterSpacing: '0.06em' }}>VS</span>
                </div>
              </div>

              <UploadSlot
                side="B" file={fileB} url={urlB}
                onFile={handleFileB}
                onUrl={v => { setUrlB(v); if (v) { setFileB(null); setFileErrB(''); } }}
                onClear={() => { setFileB(null); setUrlB(''); setFileErrB(''); }}
                drag={dragB}
                onDragOver={e => { e.preventDefault(); setDragB(true); }}
                onDragLeave={() => setDragB(false)}
                onDrop={onDropB}
                fileError={fileErrB}
              />
            </div>

            {error && (
              <p style={{ fontSize: 12, color: T.error, marginTop: 12, margin: '12px 0 0' }}>{error}</p>
            )}

            <button
              onClick={handleRunClick}
              disabled={loading}
              style={{
                width: '100%', marginTop: 16, padding: '14px 20px',
                borderRadius: 10, border: 'none',
                background: canRun ? T.accent : T.bgElevated,
                color: canRun ? '#FFFFFF' : T.placeholder,
                fontSize: 14, fontWeight: 600,
                cursor: loading ? 'not-allowed' : canRun ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'background 0.15s, color 0.15s',
                fontFamily: 'Inter, sans-serif',
                letterSpacing: '-0.01em',
              }}
              onMouseEnter={e => { if (canRun && !loading) e.currentTarget.style.background = T.accentDark; }}
              onMouseLeave={e => { if (canRun && !loading) e.currentTarget.style.background = T.accent; }}
            >
              {loading ? (
                <>
                  <div style={{
                    width: 13, height: 13, flexShrink: 0,
                    border: '2px solid rgba(255,255,255,0.25)',
                    borderTopColor: '#FFFFFF',
                    borderRadius: '50%', animation: 'spin 0.8s linear infinite',
                  }} />
                  <span>
                    {uploadPct < 100 ? `Uploading ${uploadPct}%…` : 'Almost there…'}
                  </span>
                </>
              ) : (
                buttonLabel()
              )}
            </button>

            {/* Hint below button — contextual */}
            <p style={{
              textAlign: 'center', fontSize: 11, color: T.placeholder,
              margin: '10px 0 0', fontFamily: "'IBM Plex Mono', monospace",
              letterSpacing: '0.05em', minHeight: 16,
            }}>
              {loading
                ? (uploadPct < 100
                    ? `Uploading — you'll be taken to results automatically`
                    : 'Setting up your test…')
                : !isAuthenticated
                  ? 'Sign in with Google'
                  : (user?.battles_remaining || 0) < 1
                    ? 'Choose a plan to continue'
                    : `${user.battles_remaining} test${user.battles_remaining !== 1 ? 's' : ''} remaining`}
            </p>
          </div>
        </motion.div>

        {/* Video demo — autoplay on scroll, show not tell */}
        <div style={{ marginTop: 88 }}>
          <VideoDemo />
        </div>

        {/* Demo chart — what the output looks like */}
        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.2 }}
        >
          <DemoSection />
        </motion.div>

        {/* Tech footnote */}
        <p style={{
          textAlign: 'center', fontSize: 10, color: T.placeholder,
          fontFamily: "'IBM Plex Mono', monospace",
          letterSpacing: '0.1em', marginTop: 48,
        }}>
          TRIBE V2 · FACEBOOK RESEARCH fMRI · 256-VERTEX CORTICAL MODEL · T4 GPU
        </p>
      </div>

      <AuthModal
        open={authOpen}
        onClose={() => { setAuthOpen(false); pendingRunRef.current = false; }}
        onSuccess={handleAuthSuccess}
      />
      <PricingModal
        open={pricingOpen}
        onClose={() => setPricingOpen(false)}
        onPurchased={handlePurchased}
      />
    </motion.div>
  );
}
